import { useEffect, useMemo, useState } from "react";
import styles from "./MapFiltersPanel.module.css";

import {
    getSelection,
    toggleCheckpointInGeozone,
    toggleContractor,
    toggleGeozone,
    toggleGeozoneInGroup,
    toggleGroup,
    toggleMachineInContractor,
} from "../../services/mapSelectionStorage";
import {
    ContainerPlatformsGroupForPanel,
    GeofenceGroupForPanel,
    VehicleGroupForPanel,
} from "../../../../types/api/map/map.types";

type Props = {
    geofences: GeofenceGroupForPanel[];
    machines: VehicleGroupForPanel[];
    containerPlatforms: ContainerPlatformsGroupForPanel[];
};

type TabKey = "groups" | "geozones" | "vehicle";

function sortByString<T>(items: T[], get: (item: T) => string): T[] {
    return [...items].sort((a, b) => get(a).localeCompare(get(b)));
}

export function MapFiltersPanel({ geofences, machines, containerPlatforms }: Props) {
    const [tab, setTab] = useState<TabKey>("groups");
    const [, setVersion] = useState(0);
    const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
    const [collapsedGeozones, setCollapsedGeozones] = useState<Record<string, boolean>>({});
    const [collapsedVehicles, setCollapsedVehicles] = useState<Record<string, boolean>>({});

    const toggleCollapse = (
        scope: "groups" | "geozones" | "vehicle",
        id: string
    ) => {
        if (scope === "groups") {
            setCollapsedGroups((p) => ({ ...p, [id]: !p[id] }));
            return;
        }
        if (scope === "geozones") {
            setCollapsedGeozones((p) => ({ ...p, [id]: !p[id] }));
            return;
        }
        setCollapsedVehicles((p) => ({ ...p, [id]: !p[id] }));
    };

    useEffect(() => {
        const handle = () => setVersion((v) => v + 1);
        window.addEventListener("map-selection-changed", handle);
        return () => window.removeEventListener("map-selection-changed", handle);
    }, []);

    const selection = useMemo(() => getSelection(), []);

    const geofenceGroups = useMemo(() => {
        return sortByString(
            (geofences ?? []).map((g) => ({
                id: g.groupGeofenceId,
                name: g.groupName,
                geofences: sortByString(g.geofences ?? [], (x) => x.name),
            })),
            (x) => x.name
        );
    }, [geofences]);

    const containerPlatformsGroups = useMemo(() => {
        return sortByString(
            (containerPlatforms ?? []).map((g) => ({
                id: g.geofenceId, // ключ = geofenceId
                name: g.groupName, // отображаемое имя геозоны
                cps: sortByString(g.containerPlatforms ?? [], (x) => x.name),
            })),
            (x) => x.name
        );
    }, [containerPlatforms]);

    const vehicleGroups = useMemo(() => {
        return sortByString(
            (machines ?? []).map((g) => ({
                id: g.counterpartyVehicleId,
                name: g.groupName,
                vehicles: sortByString(g.vehicles ?? [], (v) => v.number),
            })),
            (x) => x.name
        );
    }, [machines]);

    const renderGroupTab = () => (
        <div className={styles.panelBody}>
            {geofenceGroups?.length === 0 && <div className={styles.emptyText}>Нет геозон</div>}

            {geofenceGroups?.map((group) => {
                const stored = selection.geozoneGroups.find((g) => g.groupId === group.id);
                const showAll = stored?.showAll ?? false;
                const selected = showAll ? [] : stored?.geozoneIds ?? [];
                const allZoneIds = group.geofences.map((z) => z.id);

                const isCollapsed = Boolean(collapsedGroups[group.id]);

                return (
                    <div key={group.id} className={styles.groupBlock}>
                        <div className={styles.groupHeader}>
                            <button
                                type="button"
                                className={styles.collapseBtn}
                                onClick={() => toggleCollapse("groups", group.id)}
                                title={isCollapsed ? "Развернуть" : "Свернуть"}
                            >
                <span
                    className={`${styles.chevron} ${isCollapsed ? styles.chevronCollapsed : ""}`}
                >
                  ▾
                </span>
                                <span className={styles.groupTitle}>{group.name}</span>
                            </button>

                            <label
                                className={styles.toggle}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <input
                                    className={styles.toggleInput}
                                    type="checkbox"
                                    checked={showAll}
                                    onChange={() => toggleGroup(group.id)}
                                />
                                <span>Показать все</span>
                            </label>
                        </div>

                        {!isCollapsed && (
                            <div className={styles.items}>
                                {group.geofences.map((zone) => (
                                    <label key={zone.id} className={styles.item}>
                                        <input
                                            className={styles.checkbox}
                                            type="checkbox"
                                            checked={showAll || selected.includes(zone.id)}
                                            onChange={() => toggleGeozoneInGroup(group.id, zone.id, allZoneIds)}
                                        />
                                        <span className={styles.itemLabel}>{zone.name}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );

    const renderGeozonesTab = () => (
        <div className={styles.panelBody}>
            {containerPlatformsGroups?.length === 0 && <div className={styles.emptyText}>Нет КП</div>}

            {containerPlatformsGroups?.map((zone) => {
                const stored = selection.containersSites.find((g) => g.geozoneId === zone.id);
                const showAll = stored?.showAll ?? false;
                const selected = showAll ? [] : stored?.checkpointIds ?? [];
                const allCpIds = zone.cps.map((cp) => cp.id);

                const isCollapsed = Boolean(collapsedGeozones[zone.id]);

                return (
                    <div key={zone.id} className={styles.groupBlock}>
                        <div className={styles.groupHeader}>
                            <button
                                type="button"
                                className={styles.collapseBtn}
                                onClick={() => toggleCollapse("geozones", zone.id)}
                                title={isCollapsed ? "Развернуть" : "Свернуть"}
                            >
                <span
                    className={`${styles.chevron} ${isCollapsed ? styles.chevronCollapsed : ""}`}
                >
                  ▾
                </span>
                                <span className={styles.groupTitle}>{zone.name}</span>
                            </button>

                            <label
                                className={styles.toggle}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <input
                                    className={styles.toggleInput}
                                    type="checkbox"
                                    checked={showAll}
                                    onChange={() => toggleGeozone(zone.id)}
                                />
                                <span>Все КП</span>
                            </label>
                        </div>

                        {!isCollapsed && (
                            <div className={styles.items}>
                                {zone.cps.length === 0 && (
                                    <div className={styles.emptyText}>Нет контейнерных площадок</div>
                                )}
                                {zone.cps.map((cp) => (
                                    <label key={cp.id} className={styles.item}>
                                        <input
                                            className={styles.checkbox}
                                            type="checkbox"
                                            checked={showAll || selected.includes(cp.id)}
                                            onChange={() => toggleCheckpointInGeozone(zone.id, cp.id, allCpIds)}
                                        />
                                        <span className={styles.itemLabel}>{cp.name}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );

    const renderMachinesTab = () => (
        <div className={styles.panelBody}>
            {vehicleGroups?.length === 0 && <div className={styles.emptyText}>Нет машин</div>}

            {vehicleGroups?.map((group) => {
                const stored = selection.vehicle.find((c) => c.contractorId === group.id);
                const showAll = stored?.showAll ?? false;
                const selected = showAll ? [] : stored?.machineIds ?? [];
                const allVehicleIds = group.vehicles.map((v) => v.number);

                const isCollapsed = Boolean(collapsedVehicles[group.id]);

                return (
                    <div key={group.id} className={styles.groupBlock}>
                        <div className={styles.groupHeader}>
                            <button
                                type="button"
                                className={styles.collapseBtn}
                                onClick={() => toggleCollapse("vehicle", group.id)}
                                title={isCollapsed ? "Развернуть" : "Свернуть"}
                            >
                <span
                    className={`${styles.chevron} ${isCollapsed ? styles.chevronCollapsed : ""}`}
                >
                  ▾
                </span>
                                <span className={styles.groupTitle}>{group.name}</span>
                            </button>

                            <label
                                className={styles.toggle}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <input
                                    className={styles.toggleInput}
                                    type="checkbox"
                                    checked={showAll}
                                    onChange={() => toggleContractor(group.id)}
                                />
                                <span>Все машины</span>
                            </label>
                        </div>

                        {!isCollapsed && (
                            <div className={styles.items}>
                                {group.vehicles.map((v) => (
                                    <label key={v.id} className={styles.item}>
                                        <input
                                            className={styles.checkbox}
                                            type="checkbox"
                                            checked={showAll || selected.includes(v.number)}
                                            onChange={() => toggleMachineInContractor(group.id, v.number, allVehicleIds)}
                                        />
                                        <span className={styles.itemLabel}>{v.number}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );

    return (
        <div className={styles.wrapper}>
            <div className={styles.tabs}>
                <button
                    type="button"
                    className={`${styles.tab} ${tab === "groups" ? styles.tabActive : ""}`}
                    onClick={() => setTab("groups")}
                >
                    Геозоны
                </button>
                <button
                    type="button"
                    className={`${styles.tab} ${tab === "geozones" ? styles.tabActive : ""}`}
                    onClick={() => setTab("geozones")}
                >
                    КП
                </button>
                <button
                    type="button"
                    className={`${styles.tab} ${tab === "vehicle" ? styles.tabActive : ""}`}
                    onClick={() => setTab("vehicle")}
                >
                    Машины
                </button>
            </div>

            <div className={styles.panel}>
                {tab === "groups" && renderGroupTab()}
                {tab === "geozones" && renderGeozonesTab()}
                {tab === "vehicle" && renderMachinesTab()}
            </div>
        </div>
    );
}
